import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { AuthenticationService } from '../auth/auth.service';
import { WorkflowResults } from '../../models/workflow/workflow-results.interface';
import { WorkflowAssignee } from '../../models/workflow/workflow-assignee.interface';
import { WorkflowQueueFilter } from './workflow-queue-filter.interface';
import { SupervisorDefaultView } from '../../models/workflow/supervisor-default-view.interface';
import { WorkType } from '../../models/workflow/worktype.interface';
import {
	DataFilter,
	WorkflowAssignmentMappingReassignRequest,
	WorkflowProcessorTimeOff,
	WorkflowTaskOverviewResult,
	ClientIds
} from '@edassist/api';
import { WorkflowAssignmentMappingRequest } from '../../models/workflow/workflow-queue-assignment-mapping-request.interface';
import { WorkflowAssignmentMappingResults } from '../../models/workflow/workflow-queue-assignment-mapping-result.interface';
import { withCache } from '@ngneat/cashew';
import { WorkflowSupervision, WorkflowSupervisorAssignee } from '../../models/workflow/workflow-supervision.interface';
import { WorkflowProcessorTimeOffRequest } from '../../models/workflow/workflow-processor-time-off-request.interface';
import { WorkflowProcessorTimeOffResults } from '../../models/workflow/workflow-processor-time-off-result.interface';
import { constants } from '@edassist/common';
import { ApplicationWorkflow } from './application-workflow.interface';

@Injectable({ providedIn: 'root' })
export class WorkflowService {
	constructor(
		private readonly http: HttpClient,
		private readonly authService: AuthenticationService
	) {}

	getWorkflow(
		index = 1,
		recordsPerPage = 10,
		activeTasks = true,
		defaultSearch = true,
		showOnHomePage = false
	): Observable<WorkflowResults> {
		return this.retrieveWorkflowQueue(
			index,
			recordsPerPage,
			activeTasks,
			defaultSearch,
			undefined,
			undefined,
			undefined,
			undefined,
			[this.authService.currentUserValue.userId],
			showOnHomePage
		);
	}

	retrieveWorkflowQueue(
		index = 1,
		recordsPerPage = 10,
		activeTasks = true,
		defaultSearch,
		clientIdArray?,
		programTypeIdArray?,
		applicationStatusIdArray?,
		workTypeIdArray?,
		assigneeIdArray?,
		showOnHomePage = false,
		otherUsers = false,
		slaDaysArray?
	): Observable<WorkflowResults> {
		const workflowQueueFilter: WorkflowQueueFilter = {
			clientIds: clientIdArray,
			programTypeIds: programTypeIdArray,
			applicationStatusIds: applicationStatusIdArray,
			workTypeIds: workTypeIdArray,
			userIds: assigneeIdArray,
			slaDays: slaDaysArray
		};

		return this.http.post<WorkflowResults>(
			`workflow/tasks/search?index=${index}&recordsPerPage=${recordsPerPage}&activeTasks=${activeTasks}&defaultSearch=${defaultSearch}&showOnHomePage=${showOnHomePage}&otherUsers=${otherUsers}`,
			workflowQueueFilter
		);
	}

	getWorkTypes(): Observable<Array<WorkType>> {
		return this.http.get<Array<WorkType>>('workflow/work-types', {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: 'getWorkTypes'
			})
		});
	}

	getWorkflowProcessorsBySupervisorId(supervisorUserId: number): Observable<WorkflowAssignee> {
		return this.http.get<WorkflowAssignee>(`workflow/supervisors/${supervisorUserId.toString()}/processors`, {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: `getWorkflowProcessorsBySupervisorId-${supervisorUserId}`
			})
		});
	}

	getSupervisorDefaultView(userId: number, filterTypeId: number): Observable<SupervisorDefaultView> {
		return this.http.get<SupervisorDefaultView>(`workflow/supervisors/${userId}/default-view?filterTypeId=${filterTypeId}`);
	}

	getApplicationWorkflow(applicationId: number): Observable<Array<ApplicationWorkflow>> {
		return this.http.get<Array<ApplicationWorkflow>>(`workflow/applications/${applicationId}`);
	}

	saveSupervisorDefaultView(
		userId: number,
		supervisorDefaultView: SupervisorDefaultView
	): Observable<SupervisorDefaultView> {
		return this.http.post<SupervisorDefaultView>(`workflow/supervisors/${userId}/default-view`, supervisorDefaultView);
	}

	deleteSupervisorDefaultView(userId: number, filterTypeId: number): Observable<void> {
		return this.http.delete<void>(
			`workflow/supervisors/${userId}/default-view?filterTypeId=${filterTypeId}`
		);
	}

	getSupervisorDefaultViewResults(userId: number, index = 1, recordsPerPage = 2000): Observable<WorkflowResults> {
		return this.http.get<WorkflowResults>(
			`workflow/supervisors/${userId}/default-view-results?index=${index}&recordsPerPage=${recordsPerPage}`
		);
	}

	reassignWorkflowQueueTasks(workflowQueueIds: Array<number>, assignedUserId: number): Observable<void> {
		return this.http.put<void>(
			`workflow/tasks/reassign/?workflowQueueIds=${workflowQueueIds?.toString()}&assignedUserId=${assignedUserId?.toString()}`,
			undefined
		);
	}

	getDataFilters(page: string, tab: string, filter: string): Observable<Array<DataFilter>> {
		return this.http.get<Array<DataFilter>>(
			`data-filters?page=${page}&tab=${tab ? tab : ''}&filter=${filter ? filter : ''}`,
			{
				context: withCache({
					version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
					key: `getDataFilters-${page}-${tab}-${filter}`
				})
			}
		);
	}

	getWorkflowAssignmentMappings(
		workflowAssignmentMappingRequest: WorkflowAssignmentMappingRequest,
		index: number,
		recordsPerPage: number
	): Observable<WorkflowAssignmentMappingResults> {
		return this.http.post<WorkflowAssignmentMappingResults>(
			`workflow/assignment-mappings/search?index=${index}&recordsPerPage=${recordsPerPage}`,
			workflowAssignmentMappingRequest
		);
	}

	generateNewWorkflow(
		workflowAssignmentMappingRequest: WorkflowAssignmentMappingRequest
	): Observable<WorkflowAssignmentMappingResults> {
		return this.http.post<WorkflowAssignmentMappingResults>(
			'workflow/assignment-mappings/generate',
			workflowAssignmentMappingRequest
		);
	}

	getWorkflowSupervision(userId: number, isCache: boolean): Observable<WorkflowSupervision> {
		return this.http.get<WorkflowSupervision>(`workflow/supervisors/${userId}/supervision-users`, {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: `getWorkflowSupervision-${userId}`,
				cache: isCache
			})
		});
	}

	reassignWorkflowAssignmentMappings(
		reassignWorkflowAssignmentMappingRequest: WorkflowAssignmentMappingReassignRequest
	): Observable<void> {
		return this.http.put<void>('workflow/assignment-mappings/reassign', reassignWorkflowAssignmentMappingRequest);
	}

	addSupervisionUsers(supervisionUsers: Array<WorkflowSupervisorAssignee>): Observable<void> {
		return this.http.post<void>('workflow/supervisors/supervision-users', supervisionUsers);
	}

	deleteSupervisionUsers(supervisionUsers: Array<WorkflowSupervisorAssignee>): Observable<void> {
		const options = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			}),
			body: supervisionUsers
		};

		return this.http.delete<void>('workflow/supervisors/supervision-users', options);
	}

	getWorkflowProcessorTimeOff(
		workflowProcessorTimeOffRequest: WorkflowProcessorTimeOffRequest,
		index: number,
		recordsPerPage: number
	): Observable<WorkflowProcessorTimeOffResults> {
		return this.http.post<WorkflowProcessorTimeOffResults>(
			`workflow/processors/time-off/search?index=${index}&recordsPerPage=${recordsPerPage}`,
			workflowProcessorTimeOffRequest
		);
	}

	addWorkflowProcessorTimeOffs(workflowProcessorTimeOffs: Array<WorkflowProcessorTimeOff>): Observable<void> {
		return this.http.post<void>('workflow/processors/time-off/add', workflowProcessorTimeOffs);
	}

	updateWorkflowProcessorTimeOffs(workflowProcessorTimeOffs: Array<WorkflowProcessorTimeOff>): Observable<void> {
		return this.http.put<void>('workflow/processors/time-off', workflowProcessorTimeOffs);
	}

	deleteTimeOffs(processorDayOffIds: Array<number>): Observable<void> {
		return this.http.delete<void>('workflow/processors/time-off', {
			params: { processorDayOffIds }
		});
	}

	getSupervisorSummary(
		userId: number,
		isLoanProcess: boolean,
		clientList: ClientIds,
		isSupervisorTeamTasks: boolean
	): Observable<WorkflowTaskOverviewResult> {
		return this.http.post<WorkflowTaskOverviewResult>(
			`workflow/tasks/overview?userId=${userId}&activeTasks=true&index=1&recordsPerPage=-1&defaultSearch=false&loanProcessor=${isLoanProcess}&isSupervisorTeamTasks=${isSupervisorTeamTasks}`,
			clientList
		);
	}
}
